import React, { useEffect, useState } from "react";
import { Card, Table, Tag, Button } from "antd";
import { PageHeader } from "@ant-design/pro-layout";
import {
  AccountBookOutlined,
  EyeOutlined,
  HomeOutlined,
} from "@ant-design/icons";
import CustomBreadcrumb from "../../../components/CustomBreadcrumb";
import { useTranslation } from "react-i18next";
import { getinvoices } from "../../../network/network";
import {
  InvoiceDetailType,
  InvoiceStatus,
} from "../../../configs/constants";
import HyperPayPaymentModal from "../../../components/HyperPayPaymentModal";
import moment from "moment";
import CurrencyFormatter from "../../../components/CurrencyFormatter";
import { handleApiError, showAppError } from "../../../utils/errorHandler";

const Invoices = () => {
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [invoicesTableData, setinvoicesTableData] = useState([]);
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [invoiceId, setInvoiceId] = useState(false);
  const [invoiceAmount, setInvoiceAmount] = useState(0);
  const { t } = useTranslation();

  const getInvoiceDetailTag = (detail, index) => {
    const detailMeta = {
      [InvoiceDetailType.Subscription]: { color: "green" },
      [InvoiceDetailType.License]: { color: "blue", showQuantity: true },
      [InvoiceDetailType.Discount]: { color: "gold" },
      [InvoiceDetailType.Tax]: { color: "cyan" },
      [InvoiceDetailType.Addon]: { color: "purple", showQuantity: true },
      [InvoiceDetailType.BalanceTopup]: { color: "orange" },
    };
    const meta = detailMeta[detail.type] || { color: "default" };
    const quantity = Number(detail.quantity);
    const showQuantity =
      meta.showQuantity && Number.isFinite(quantity) && quantity > 0;

    return (
      <Tag color={meta.color} key={`${detail.id || "detail"}-${index}`}>
        {showQuantity ? (
          <span dir="ltr">
            {quantity} x <bdi dir="auto">{detail.item || "-"}</bdi>
          </span>
        ) : (
          detail.item || "-"
        )}
      </Tag>
    );
  };

  useEffect(() => {
    // Set Loading True
    setIsDataLoading(true);
    getinvoices()
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          return;
        }

        let invoices = response.data.data.invoices;
        // This will hold all of the invoices being modified
        let invoices_data = [];
        invoices.forEach((invoice) => {
          // details will hold all details being simplified
          let details = [];
          invoice.details.forEach((detail) => {
            if (details.some((e) => e.item === detail.item)) {
              var objIndex = details.findIndex(
                (obj) => obj.item === detail.item,
              );
              details[objIndex].quantity =
                details[objIndex].quantity + detail.quantity;
            } else {
              details.push(detail);
            }
          });
          invoice.details = details;
          invoices_data.push(invoice);
        });
        setinvoicesTableData(invoices_data);
      })
      .catch((error) => handleApiError(error, t))
      .finally(() => {
        setIsDataLoading(false);
      });
  }, [t]);

  const showPaymentModal = async (invoice) => {
    if (invoice && invoice.amount_charged > 0) {
      setPaymentModalVisible(true);
      setInvoiceId(invoice.id);
      setInvoiceAmount(invoice.amount_charged);
    } else {
      // Error
      showAppError(t("something_went_wrong"));
    }
  };

  const breadcrumbRoutes = [
    {
      path: "/",
      breadcrumbName: t("home"),
      icon: <HomeOutlined />,
    },
    {
      path: "invoices",
      breadcrumbName: t("invoices"),
      icon: <AccountBookOutlined />,
    },
  ];

  const invoicesTableColumns = [
    {
      title: "#",
      key: "index",
      className: "app-table-index-column",
      render: (text, record, index) => {
        return <p>{index + 1}</p>;
      },
    },
    {
      title: t("invoice_id"),
      dataIndex: "uid",
      render: (uid) => {
        return uid ? uid : "-";
      },
    },
    {
      title: t("items"),
      render: (invoices) => {
        return (
          <>
            {(invoices.details || []).map(getInvoiceDetailTag)}
          </>
        );
      },
    },
    {
      title: t("total_amount_charged"),
      render: (invoices) => {
        return <CurrencyFormatter value={invoices.amount_charged} />;
      },
    },
    {
      title: t("date"),
      dataIndex: "created_at",
      render: (created_at) => {
        return created_at
          ? moment(created_at).format("MMM. D, YYYY hh:mm a")
          : "-";
      },
    },
    {
      title: t("status"),
      render: (invoices) => {
        const statusMeta = {
          [InvoiceStatus.Cancelled]: {
            color: "red",
            label: t("cancelled"),
          },
          [InvoiceStatus.Paid]: { color: "green", label: t("paid") },
          [InvoiceStatus.Refunded]: {
            color: "blue",
            label: t("refunded"),
          },
          [InvoiceStatus.Unpaid]: { color: "red", label: t("unpaid") },
          [InvoiceStatus.RechargeRequest]: {
            color: "orange",
            label: t("recharge_request"),
          },
        };
        const meta = statusMeta[invoices.status];

        return meta ? <Tag color={meta.color}>{meta.label}</Tag> : "-";
      },
    },
    {
      title: t("reference_no"),
      dataIndex: "stcpay_reference_id",
      render: (stcpay_reference_id) => {
        return stcpay_reference_id ? stcpay_reference_id : "-";
      },
    },
    {
      title: t("payment_type"),
      dataIndex: "payment_brand",
      render: (payment_brand) => {
        return payment_brand ? payment_brand : "-";
      },
    },
    {
      title: t("invoices"),
      render: (invoice) => {
        return (
          <>
            {invoice.status === InvoiceStatus.Unpaid &&
              !invoice.stcpay_reference_id && (
                <Button
                  type="primary"
                  style={{
                    backgroundColor: "var(--color-success-hover)",
                    border: "var(--color-success-hover)",
                    marginBottom: "10px",
                  }}
                  size="small"
                  onClick={() => showPaymentModal(invoice)}
                >
                  {t("pay_now")}!
                </Button>
              )}
            <div>
              <Button
                type="default"
                size="small"
                target="_blank"
                href={
                  process.env.REACT_APP_API_ROOT +
                  "/companies/" +
                  invoice.company.id +
                  "/invoices/" +
                  invoice.id
                }
              >
                <EyeOutlined />
                {t("view_invoice")}
              </Button>
            </div>
          </>
        );
      },
    },
  ];
  return (
    <>
      <Card className="app-mb-20">
        <CustomBreadcrumb menus={breadcrumbRoutes} />
        <PageHeader
          className="app-p-0"
          title={t("invoices")}
          ghost={false}
        />
      </Card>
      <Card>
        <Table
          columns={invoicesTableColumns}
          dataSource={invoicesTableData}
          loading={isDataLoading}
          rowKey="id"
          pagination={false}
        />
      </Card>
      {invoiceId && (
        <HyperPayPaymentModal
          invoiceId={invoiceId}
          amount={invoiceAmount}
          paymentModalVisible={paymentModalVisible}
          setPaymentModalVisible={setPaymentModalVisible}
        />
      )}
    </>
  );
};

export default Invoices;
